// import pdpQuery from '~layers/graphql/pdp.gql'
import localize from '~layers/utils/localize.ts'

export function usePDP(meta: { value?: { sku?: string, filters: any[] } }) {
  const productData = useState('productData', () => null)
  const productError = useState<unknown>('productError', () => null)
  const pdpFixedContentData = useState('pdpFixedContentData', () => null)
  const pdpFixedContentError = useState<unknown>('pdpFixedContentError', () => null)
  const pdpDynamicContentData = useState('pdpDynamicContentData', () => null)
  const pdpDynamicContentError = useState<unknown>('pdpDynamicContentError', () => null)
  const stockData = useState('stockData', () => ({}))
  const pendingStock = useState('pendingStock', () => false)

  // const { public: config } = useRuntimeConfig()
  // const locale = config.strapiLocale
  // const status = 'PUBLISHED'

  // const variables = {
  //   sku: meta.value?.sku,
  //   locale,
  //   status,
  //   plpUrls: meta.value?.parents?.map((parent: any) => parent.url) || [],
  // }

  const routeFilters = computed<Record<string, string[]>>(() => {
    return meta.value?.filters?.reduce((acc: Record<string, string[]>, filter: { key: string, values: string[] }) => {
      acc[filter.key] = filter.values
      return acc
    }, {}) || {}
  })

  const uspsToRequest = computed<string>(() => {
    const saleType = routeFilters.value.saleType?.[0] || ''
    return saleType ? `usps_pdp_${saleType.toLowerCase()}` : 'usps_pdp'
  })

  const slicesToRequest = computed<string>(() => {
    const groupCode = routeFilters.value.groupCode?.[0] || ''
    return groupCode ? `pdp_${groupCode}` : 'pdp'
  })

  async function fetchProductData({ sku, ...args }: { sku: string }) {
    // const { data, error } = await useAsyncQuery<any>(pdpQuery, variables)
    const result = await useGraphqlQuery('pdp', {
      sku,
      ...args,
    }, useGraphqlDefaultClient())

    if (!result.data) {
      return
    }

    productData.value = localize(result.data)
    productError.value = result.errors
  }

  async function fetchFixedContent({ locale, status }: { locale: string, status: string }) {
    const result = await useGraphqlQuery('pdpFixedContent', {
      locale,
      status,
      usps: uspsToRequest.value,
      slices: slicesToRequest.value,
    }, useGraphqlDefaultClient())

    if (!result.data) {
      return
    }

    pdpFixedContentData.value = localize(result.data)
    pdpFixedContentError.value = result.errors
  }

  async function fetchDynamicContent({ sku, locale, status }: { sku: string, locale: string, status: string }) {
    const result = await useGraphqlQuery('pdpDynamicContent', {
      sku,
      locale,
      status,
    }, useGraphqlDefaultClient())

    if (!result.data) {
      return
    }

    pdpDynamicContentData.value = localize(result.data)
    pdpDynamicContentError.value = result.errors
  }

  const esProduct = computed(() => {
    return productData?.value?.esProduct
  })

  const media = useBynderAssets(esProduct)

  const usps = computed(() => {
    return pdpFixedContentData?.value?.pdpUsps?.[0]
  })

  const m2Product = computed(() => {
    const product = {
      ...(productData?.value?.products?.items?.[0] || {}),
    }
    delete product.categories

    if (esProduct.value) {
      product.product = esProduct.value
    }

    if (stockData.value?.products?.items?.[0]?.variants?.length) {
      product.variants = product.variants?.map((variant, index) => ({ ...variant, product: { ...variant.product, ...stockData.value?.products?.items?.[0]?.variants[index].product } }))
    }
    if (stockData.value?.products?.items?.[0]?.stock_status) {
      product.stockItem = stockData.value?.products?.items?.[0].stockItem
      product.stock_left = stockData.value?.products?.items?.[0].stock_left
      product.stock_status = stockData.value?.products?.items?.[0].stock_status
    }
    if (stockData.value?.products?.items?.[0]?.items) {
      product.items = result.items.map((item, index) => {
        if (stockData.value?.products?.items?.[0]?.items[index]?.product?.variants?.length) {
          return {
            ...item,
            product: {
              ...item.product,
              ...stockData.value?.products?.items?.[0]?.items[index]?.product,
              variants: item.product.variants.map((variant, vIndex) => ({
                ...variant,
                product: { ...variant.product, ...stockData.value?.products?.items?.[0]?.items[index]?.product.variants[vIndex].product },
              })),
            },
          }
        }
        if (stockData.value?.products?.items?.[0]?.items[index]?.product?.stock_status) {
          return { ...item, product: { ...item.product, ...stockData.value.products.items[0].items[index].product } }
        }
        return { ...item }
      })
    }
    return product
  })

  const bynderAssets = computed(() => {
    if (!m2Product.value?.bynderAssets?.length) {
      return null
    }

    return m2Product.value?.bynderAssets.filter((asset) => {
      return !['Foto', 'Video', '3D'].includes(asset.type.at(0) || '')
    }).map((asset) => {
      return {
        name: asset.name,
        src: asset.files.find(file => file.key === 'original')?.value,
        thumbnail: asset.files.find(file => file.key === 'thul')?.value || '',
        // ...asset,
      }
    }).sort((a, b) => a.thumbnail.localeCompare(b.thumbnail))
  })

  const crosssellProducts = computed(() =>
    m2Product.value?.crosssell_products?.map(item => item.product) || [],
  )

  const upsellProducts = computed(() =>
    m2Product.value?.upsell_products?.map(item => item.product) || [],
  )

  const relatedProducts = computed(() =>
    m2Product.value?.related_products?.map(item => item.product) || [],
  )

  const interestingProducts = computed(() =>
    m2Product.value?.interesting_products?.map(item => item.product) || [],
  )

  const attributes = computed<Record<string, string>>(() => {
    return m2Product.value?.attributes?.items?.reduce<Record<string, string>>((acc, attribute) => {
      if ('value' in attribute && attribute.value) {
        acc[attribute.code] = attribute.value
      }
      else if ('selected_options' in attribute && attribute.selected_options) {
        acc[attribute.code] = attribute.selected_options.map(option => option.label).join(', ')
      }
      return acc
    }, {}) || {}
  })

  const categories = computed(() => {
    return [
      ...(productData?.value?.products?.items?.[0]?.categories || []),
    ].filter(item => item.url_path).sort((a: { level: number }, b: { level: number }) => b.level - a.level).map((category: { url_path: string, name: string, level: number }) => {
      return {
        ...category,
        url: category.url_path,
        label: category.name,
        level: category.level,
      }
    })
  })

  const parentCategories = computed(() => {
    let parent: { url?: string } = {}
    const parents: Array<{ url?: string }> = []

    categories.value.forEach((category) => {
      if (category.url && (!parent.url || parent.url.startsWith(category.url))) {
        parent = category
        parents.push(category)
      }
    })

    return parents
  })

  // const productAttributeValue = (code: string) => {
  //   return m2Product.value?.attributeSetData?.filter((attribute: { code: string, value: unknown }) => attribute.code === code)[0]?.value
  // }

  const contentBlocks = computed(() => {
    return pdpFixedContentData?.value?.contentBlocks?.[0] || null
  })

  const shopTheLook = computed(() => {
    return pdpDynamicContentData?.value?.shopTheLooks?.[0] || null
  })

  const deliveryAndReturns = computed(() => {
    return pdpFixedContentData?.value?.tooltips?.find(tooltip => tooltip.UID === 'delivery-and-returns') || null
  })

  const stockAndDelivery = computed(() => {
    return pdpFixedContentData?.value?.tooltips?.find(tooltip => tooltip.UID === 'stock-and-delivery') || null
  })

  const loadStockData = () => {
    pendingStock.value = true
    useGraphqlQuery('productStock', { sku: meta.value?.sku }, useGraphqlCustomerClient()).then((response) => {
      stockData.value = response.data
    }).finally(() => {
      pendingStock.value = false
    })
  }

  return {
    data: productData,
    attributes,
    esProduct,
    m2Product,
    categories,
    contentBlocks,
    parentCategories,
    error: productError,
    crosssellProducts,
    bynderAssets,
    upsellProducts,
    relatedProducts,
    interestingProducts,
    usps,
    media,
    shopTheLook,
    deliveryAndReturns,
    stockAndDelivery,
    fetchProductData,
    fetchFixedContent,
    fetchDynamicContent,
    stockData,
    loadStockData,
    pendingStock,
  }
}
